/**
 * Полоса «Что дальше» окна макета — первое, что видно под шапкой.
 *
 * Показывает, где макет в конвейере, что произойдёт по главной кнопке, и
 * держит рядом ошибку или комментарий возврата. С 11.10.2026 рисуется общим
 * `NextStepBar` окон сущностей: те же действия по `stagePlan`, к ним добавлены
 * чипы проверок (исходник, атрибуты, комплект, файлы печати, визуалы,
 * карточки) — клик по чипу ведёт на нужную вкладку.
 */

import React from 'react'
import { Loader2 } from 'lucide-react'
import {
  NextStepBar,
  type NextStepAction,
  type NextStepCheck,
  type NextStepTone,
} from '../../../../components/ui/NextStepBar'
import type { StageAction, StageContext, StagePlan } from './stageActions'
import { PipelineErrorPlate, type PipelineNoticeValue } from './PipelineErrorPlate'

interface StageActionBarProps {
  plan: StagePlan
  context: StageContext
  /** Идёт какое-то действие конвейера */
  busy: boolean
  /** Какое именно действие выполняется (для спиннера на нужной кнопке) */
  busyKind?: string | null
  onRun: (action: StageAction) => void
  /** Ошибка конвейера или комментарий возврата дизайнеру */
  notice?: PipelineNoticeValue | null
  /** Сырой текст ошибки конвейера: из него строится разбор по площадкам. */
  rawError?: string | null
  /** Префикс категории по артикулу — для краткой подписи групп ошибки. */
  prefixOf?: (article: string) => string | null
  onOpenProduct?: (article: string) => void
  /** Стадия ещё не загружена */
  loading?: boolean
  /** Локальные действия окна, которые должны стоять прямо перед главной кнопкой. */
  beforePrimary?: NextStepAction[]
  /** Чипы проверок под строкой действия. */
  checks?: NextStepCheck[]
}

const toneOf = (plan: StagePlan, notice?: PipelineNoticeValue | null): NextStepTone => {
  if (notice?.kind === 'error') return 'bad'
  if (plan.tone === 'problem') return 'warn'
  if (plan.tone === 'decision') return 'info'
  return 'neutral'
}

export const StageActionBar: React.FC<StageActionBarProps> = ({
  plan,
  context,
  busy,
  busyKind,
  onRun,
  notice,
  rawError,
  prefixOf,
  onOpenProduct,
  loading = false,
  beforePrimary = [],
  checks,
}) => {
  if (loading) {
    return (
      <div className="shrink-0 border-b border-gray-200 px-4 py-3 sm:px-5 dark:border-zinc-800">
        <div className="h-9 w-full animate-pulse rounded-md bg-gray-100 dark:bg-zinc-800" />
      </div>
    )
  }

  const toAction = (action: StageAction, variant: NextStepAction['variant']): NextStepAction => ({
    key: action.kind,
    label: action.label,
    variant,
    disabled: busy,
    loading: busy && busyKind === action.kind,
    onClick: () => onRun(action),
  })

  const actions: NextStepAction[] = [
    ...plan.secondary.map((action) => toAction(action, 'secondary')),
    ...beforePrimary.map((action) => ({ ...action, disabled: action.disabled || busy })),
    ...(plan.primary ? [toAction(plan.primary, 'primary')] : []),
  ]

  return (
    <NextStepBar
      tone={toneOf(plan, notice)}
      title={plan.headline}
      hint={
        <span className="inline-flex items-center gap-1">
          {plan.hint(context)}
          {actions.length === 0 && busy && (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" aria-hidden="true" />
          )}
        </span>
      }
      actions={actions}
      checks={checks}
      extra={
        notice ? (
          <PipelineErrorPlate
            notice={notice}
            rawError={rawError}
            prefixOf={prefixOf}
            onOpenProduct={onOpenProduct}
          />
        ) : undefined
      }
    />
  )
}

export default StageActionBar
